import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { modifyFavoritesResponse } from 'utils/sportsbook/favorites/fav-sorter';
import RidGenerator from 'utils/swarm/rid-generator';
import { TFavoriteDataResponse } from 'interfaces/sportsbook';
import { executeCommand } from 'services/get-data-level';
import { unsubscribeCommand } from 'services/get-swarm-data';
import { useEventbus } from 'newhooks/generic/useEventbus';
import {
  EFavoriteActions,
  FAVORITE_HOOKS_CACHE,
  getFavoriteSubscribeWhere
} from 'newhooks/sportsbook/favorites/sharedHelpers';
import { setFavoriteIdsLoading, setFavoriteSportsData } from 'store/actions';
import {
  getFavoriteDataLoading,
  getFavoriteMatches,
  getFavoriteMatchIds
} from 'store/selectors/fav-data';

const FAVORITE_MATCHES_RELATIONS = {
  sport: ['id', 'type'],
  game: ['id', 'type'],
  competition: ['id'],
  region: ['id']
};

export const useFetchFavoriteMatchIds = () => {
  const dispatch = useDispatch();

  useEventbus({
    [EFavoriteActions.dataSubscriptionUnmount]: () => {
      setShouldCall(true);
    }
  });

  const hookId = useRef(RidGenerator.gCustom('FAVORITE_MATCH_IDS_'));

  const [shouldCall, setShouldCall] = useState(false);

  const favoriteMatches = useSelector(getFavoriteMatches).all;
  const favoriteMatchIds = useSelector(getFavoriteMatchIds).all;
  const favoriteDataLoading = useSelector(getFavoriteDataLoading);

  useLayoutEffect(() => {
    FAVORITE_HOOKS_CACHE.mountedHookIds.fetchIds.push(hookId.current);

    return () => {
      FAVORITE_HOOKS_CACHE.mountedHookIds.fetchIds.splice(
        FAVORITE_HOOKS_CACHE.mountedHookIds.fetchIds.indexOf(hookId.current),
        1
      );
    };
  }, []);

  useLayoutEffect(() => {
    if (
      FAVORITE_HOOKS_CACHE.subscribedHookIds.fetchIds &&
      (FAVORITE_HOOKS_CACHE.mountedHookIds.fetchIds.length === 1 ||
        hookId.current === FAVORITE_HOOKS_CACHE.subscribedHookIds.fetchIds)
    ) {
      unsubscribeCommand(FAVORITE_HOOKS_CACHE.subscribedHookIds.fetchIds);
      FAVORITE_HOOKS_CACHE.subscribedHookIds.fetchIds = null;
    }
  }, [favoriteMatchIds.length]);

  useLayoutEffect(() => {
    let shouldCall = true;

    if (
      FAVORITE_HOOKS_CACHE.subscribedHookIds.fetchIds ||
      FAVORITE_HOOKS_CACHE.subscribedHookIds.fetchData ||
      favoriteDataLoading
    ) {
      shouldCall = false;
    }

    setShouldCall(shouldCall);
  }, [favoriteDataLoading]);

  useEffect(() => {
    if (
      shouldCall &&
      (FAVORITE_HOOKS_CACHE.subscribedHookIds.fetchIds ||
        FAVORITE_HOOKS_CACHE.subscribedHookIds.fetchData)
    ) {
      return setShouldCall(false);
    }

    if (favoriteMatchIds.length && shouldCall) {
      FAVORITE_HOOKS_CACHE.subscribedHookIds.fetchIds = hookId.current;

      const onResponse = ({ sport }: TFavoriteDataResponse) => {
        if (
          FAVORITE_HOOKS_CACHE.subscribedHookIds.fetchData === null &&
          favoriteMatchIds
        ) {
          dispatch(
            setFavoriteSportsData(modifyFavoritesResponse(sport || {}, true))
          );
        }

        dispatch(setFavoriteIdsLoading(false));
      };

      executeCommand(
        hookId.current,
        FAVORITE_MATCHES_RELATIONS,
        getFavoriteSubscribeWhere(favoriteMatchIds),
        onResponse,
        true,
        onResponse
      );
    }
  }, [favoriteMatchIds.length, shouldCall]);

  useLayoutEffect(() => {
    if (FAVORITE_HOOKS_CACHE.subscribedHookIds.fetchData) {
      if (FAVORITE_HOOKS_CACHE.subscribedHookIds.fetchIds) {
        unsubscribeCommand(FAVORITE_HOOKS_CACHE.subscribedHookIds.fetchIds);
        FAVORITE_HOOKS_CACHE.subscribedHookIds.fetchIds = null;
      }
    } else if (!FAVORITE_HOOKS_CACHE.subscribedHookIds.fetchIds) {
      setShouldCall(true);
    }
  }, [favoriteDataLoading, favoriteMatches]);
};
